import {
  Button,
  Drawer,
  Grid,
  Paper,
  TablePagination,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { CheckCircleRounded, Edit, FilterList, People, Send } from "@material-ui/icons";
import { format, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { FiCheckCircle } from "react-icons/fi";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useActionList } from "../../../context/lists/actionList";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { getAll } from "../../../services/actions.service";
import {
  getDateFormats,
  getDateTimeLocale,
} from "../../../utils/get-date-time-locale";
import { Table } from "../../Common/Table";
import ModalBase from "../utils/ModalBase";
import { checkAccess } from "../utils/index";
import FilterDrawer from "./FilterDrawer";
import SendActionEmailModal from "./SendActionEmailModal";
import UpdateMultipleFocalPointModal from "./UpdateMultipleFocalPointModal";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  fullWidthContainer: {
    maxWidth: "100%",
  },
  paper: {
    maxWidth: "100%",
    padding: "16px",
  },
  subtitle: {
    ...theme.typography.subtitle1,
  },
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  filterButton: {
    ...theme.typography.subtitle1,
    backgroundColor: "#fff",
    color: "#666666",
    borderRadius: 8,
    border: "1px solid #666666",
    textTransform: "uppercase",
    maxWidth: 280,
    width: "100%",
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
  sendMailIconContainer: {
    display: "flex",
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    padding: '6px'
  },
  sentMailIcon: {
    position: "absolute", 
    top: 0, 
    right: 0
  }
}));

function Actions({ history, actionShowSpinner, location }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { userAccess } = useAuth();
  const { listOptions, updateFilterParams, updatePagination } = useActionList();

  const getFormattedActionsStatus = (statusCode) => {
    switch (statusCode) {
      case "L":
        return (
          <span style={{ fontWeight: "bold", color: "#97CA83" }}>
            {t("menu.actions.actionStatus.L")}
          </span>
        );
      case "R":
        return (
          <span style={{ fontWeight: "bold", color: "#6fa5f9" }}>
            {t("menu.actions.actionStatus.R")}
          </span>
        );
      case "C":
        return (
          <span style={{ fontWeight: "bold", color: "#E11021" }}>
            {t("menu.actions.actionStatus.C")}
          </span>
        );
      case "AT":
        return (
          <span style={{ fontWeight: "bold", color: "#465eb6" }}>
            {t("menu.actions.actionStatus.AT")}
          </span>
        );
      case "CO":
        return (
          <span style={{ fontWeight: "bold", color: "#64b246" }}>
            {t("menu.actions.actionStatus.CO")}
          </span>
        );
      case "NI":
        return (
          <span style={{ fontWeight: "bold", color: "#F5D700" }}>
            {t("menu.actions.actionStatus.NI")}
          </span>
        );
    }
  };

  const columns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "committe",
    },
    {
      title: t("meeting.meetingDate").toUpperCase(),
      field: "meetDate",
    },
    {
      title: "#",
      field: "actionNumber",
      minWidth: 5,
    },
    {
      title: t("menu.slot.link.parent").toUpperCase(),
      field: "theme",
      cellStyle: {
        minWidth: "10rem",
      },
    },
    {
      title: t("forms.minutes.actions.action").toUpperCase(),
      field: "actionDescription",
      cellStyle: {
        minWidth: "30rem",
      },
    },
    {
      title: t("forms.minutes.actions.responsible").toUpperCase(),
      field: "firstResponsible",
    },
    {
      title: t("status").toUpperCase(),
      field: "status",
      cellStyle: {
        textAlign: "center",
      },
      render: (row) => getFormattedActionsStatus(row.status),
    },
    {
      title: t("forms.minutes.actions.deadline").toUpperCase(),
      field: "deadline",
    },
  ];

  const [actions, setActions] = useState([]);
  const [totalActions, setTotalActions] = useState(0);
  const [actionDeleteSuccess, setActionDeleteSuccess] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [successEditAction, setSuccessEditAction] = useState(false);
  const [openSendActionEmailModal, setOpenSendActionEmailModal] =
    useState(false);
  const [openUpdateFocalPointModal, setOpenUpdateFocalPointModal] =
    useState(false);
  const [focalPointUpdateSuccess, setFocalPointUpdateSuccess] = useState(false);

  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  const [actionToSendEmail, setActionToSendEmail] = useState();

  useEffect(() => {
    if (
      location?.state?.sccessEditAction &&
      typeof location?.state?.sccessEditAction === "boolean"
    ) {
      setSuccessEditAction(true);

      setTimeout(() => {
        setSuccessEditAction(false);
      }, 6000);
    }

    if (
      location?.state?.actionDeleteSuccess &&
      typeof location?.state?.actionDeleteSuccess === "boolean"
    ) {
      setActionDeleteSuccess(true);

      setTimeout(() => {
        setActionDeleteSuccess(false);
      }, 6000);
    }
  }, [location]);

  const fetchActions = useCallback(async () => {
    actionShowSpinner(true);

    try {
      const response = await getAll({
        ...(listOptions.filterParams.actionStatus && {
          actionStatus: listOptions.filterParams.actionStatus.map(
            (status) => status.id
          ),
        }),
        ...(listOptions.filterParams.companies && {
          companyIds: listOptions.filterParams.companies.map(
            (company) => company.id
          ),
        }),
        ...(listOptions.filterParams.responsible && {
          responsibleId: listOptions.filterParams.responsible.id,
        }),
        ...(listOptions.filterParams.requester && {
          requesterId: listOptions.filterParams.requester.id,
        }),
        ...(listOptions.filterParams.directorship && {
          directoryId: listOptions.filterParams.directorship.id,
        }),
        ...(listOptions.filterParams.committee && {
          committeeId: listOptions.filterParams.committee.id,
        }),
        ...(listOptions.filterParams.kind && {
          kind: listOptions.filterParams.kind.id,
        }),
        ...(listOptions.filterParams.actionNumber && {
          actionNumber: listOptions.filterParams.actionNumber,
        }),
        ...(listOptions.filterParams.chairpersonValidation && {
          chairpersonValidation:
            listOptions.filterParams.chairpersonValidation.id,
        }),
        ...(listOptions.filterParams.meetStartDate && {
          meetStartDate: startOfDay(listOptions.filterParams.meetStartDate),
        }),
        ...(listOptions.filterParams.meetEndDate && {
          meetEndDate: startOfDay(listOptions.filterParams.meetEndDate),
        }),
        ...(listOptions.filterParams.deadlineStartDate && {
          deadlineStartDate: startOfDay(
            listOptions.filterParams.deadlineStartDate
          ),
        }),
        ...(listOptions.filterParams.deadlineEndDate && {
          deadlineEndDate: startOfDay(listOptions.filterParams.deadlineEndDate),
        }),
        ...(listOptions.filterParams.keyWord && {
          keyWord: listOptions.filterParams.keyWord,
        }),
        ...(listOptions.filterParams?.focalPoint && listOptions.filterParams.focalPoint?.id && {
          focalPointId: listOptions.filterParams.focalPoint.id,
        }),
        pageSize: listOptions.pagination.size,
        currentPage: listOptions.pagination.page,
      })
      
      setActions(response.items);
      setTotalActions(response.countItems);
    } catch {
      actionShowSpinner(false);
    } finally {
      actionShowSpinner(false);
    }
  }, [listOptions, actionShowSpinner]);

  useEffect(() => {
    actionShowSpinner(true);

    if (userAccess) {
      const hasAccess = checkAccess(userAccess, "listActions");
      const hasAccessToSeeOwnFocalActions = checkAccess(userAccess, "updateOwnFocalActions");

      if (!hasAccess && !hasAccessToSeeOwnFocalActions) {
        history.push("/");
      }

      fetchActions();
    }
  }, [actionShowSpinner, userAccess, history, fetchActions]);

  const formattedActions = useMemo(() => {
    if (actions) {
      return actions?.map((action) => ({
        id: action.id,
        committe: action.committe,
        meetDate: action.meetDate
          ? format(new Date(action.meetDate), getDateFormats(i18n.language), {
              locale: getDateTimeLocale(i18n.language),
            })
          : format(new Date(), getDateFormats(i18n.language), {
              locale: getDateTimeLocale(i18n.language),
            }),
        deadline: action.deadline
          ? format(new Date(action.deadline), getDateFormats(i18n.language), {
              locale: getDateTimeLocale(i18n.language),
            })
          : "",
        actionNumber: action.actionNumber,
        actionDescription: action.actionDescription,
        requester: action.requester,
        firstResponsible: action.firstResponsible,
        companies: action.companies,
        secoundResponsible: action.secoundResponsible,
        firstResponsibleEmail: action.firstResponsibleEmail,
        secoundResponsibleEmail: action.secoundResponsibleEmail,
        theme: action.theme,
        status: action.status,
        emailSent: action.emailSent,
      }));
    } else {
      return [];
    }
  }, [actions, i18n]);

  return (
    <Grid container className={classes.root}>
      <ModalBase
        open={openSendActionEmailModal}
        setOpen={setOpenSendActionEmailModal}
        content={
          <SendActionEmailModal
            t={t}
            setModalStatus={(value) => setOpenSendActionEmailModal(value)}
            bodyEmail=""
            actionInfo={actionToSendEmail}
            onSucces={() => {
              fetchActions();
              setOpenSendActionEmailModal(false);
              setEmailSentSuccess(true);

              setTimeout(() => setEmailSentSuccess(true), 4000);
            }}
          />
        }
      />

      <ModalBase
        open={openUpdateFocalPointModal}
        setOpen={setOpenUpdateFocalPointModal}
        content={
          <UpdateMultipleFocalPointModal
            onClose={() => setOpenUpdateFocalPointModal(false)}
            onSuccess={() => {
              fetchActions();
              setOpenUpdateFocalPointModal(false);
              setFocalPointUpdateSuccess(true);

              setTimeout(() => setFocalPointUpdateSuccess(false), 6000);
            }}
          />
        }
      />

      <Drawer
        anchor="right"
        open={filterDrawerOpened}
        onClose={() => {
          setFilterDrawerOpened(false);
        }}
      >
        <FilterDrawer
          actionShowSpinner={(opened) => actionShowSpinner(opened)}
          onClose={() => {
            setFilterDrawerOpened(false);
          }}
          t={t}
          setFilters={(newValues) => {
            updateFilterParams(newValues);
            updatePagination({ ...listOptions.pagination, page: 0 });
          }}
          filters={listOptions.filterParams}
        />
      </Drawer>

      <Grid
        container
        item
        direction="row"
        justify="space-between"
        className={classes.spacer}
      >
        <Grid item xs={12} md={6} lg={6}>
          <Typography variant="h4">{t("menu.actions.title")}</Typography>
        </Grid>
      </Grid>

      <Grid container item direction="row" spacing={2}>
        <Grid container item className={classes.fullWidthContainer}>
          <Paper elevation={1} className={classes.paper}>
            <Grid container spacing={3}>
              <Grid item lg={12} xs={10} style={{ display: "flex", gap: 12 }}>
                <Button
                  className={classes.filterButton}
                  startIcon={<FilterList style={{ fontSize: 30 }} />}
                  onClick={() => setFilterDrawerOpened(true)}
                >
                  {t("forms.filters")}
                </Button>

                {checkAccess(userAccess, "updateActionFocalPoint") && (
                  <Button
                    className={classes.filterButton}
                    startIcon={<People style={{ fontSize: 24 }} />}
                    onClick={() => setOpenUpdateFocalPointModal(true)}
                  >
                    {t("menu.actions.bulkFocalPoint.buttonLabel")}
                  </Button>
                )}
              </Grid>

              <Grid item lg={12} xs={12}>
                <Grid style={{ maxWidth: "100%" }} item md={12} lg={12}>
                  <Table
                    columns={columns}
                    data={formattedActions}
                    actions={[
                      {
                        icon: () => <Edit />,
                        tooltip: t("edit"),
                        onClick: (_event, rowData) => {
                          history.push({
                            pathname: "/action/info",
                            state: {
                              action_id: rowData.id,
                            },
                          });
                        },
                      },
                      (rowData) => ({
                        icon: () => (
                          <div className={classes.sendMailIconContainer}>
                            {rowData.emailSent && (
                              <FiCheckCircle
                                size={12}
                                color="#64B246 "
                                className={classes.sentMailIcon}
                              />
                            )}

                            <Send />
                          </div>
                        ),
                        tooltip: t("menu.actions.sendEmail"),
                        onClick: (_event, action) => {
                          setOpenSendActionEmailModal(true);
                          setActionToSendEmail(action);
                        },
                      }),
                    ]}
                    pageSize={listOptions.pagination.size}
                    components={{
                      Pagination: (props) => {
                        return (
                          <TablePagination
                            style={{ float: "right" }}
                            rowsPerPageOptions={[5, 10, 20]}
                            rowsPerPage={listOptions.pagination.size}
                            page={listOptions.pagination.page}
                            labelRowsPerPage={t("table.labelRowsPerPage")}
                            labelDisplayedRows={(_data) => {
                              let numberOfPages = Math.ceil(
                                totalActions / listOptions.pagination.size
                              );

                              if (numberOfPages <= 1) {
                                numberOfPages = 1;
                              }

                              return `${listOptions.pagination.page + 1} ${t(
                                "table.labelDisplayedRows"
                              )} ${numberOfPages}`;
                            }}
                            onPageChange={(_e, page) => {
                              updatePagination({
                                page: page,
                                size: listOptions.pagination.size,
                              });
                            }}
                            onRowsPerPageChange={(event) => {
                              const newSize = event.target.value;

                              updatePagination({
                                page: 0,
                                size: newSize,
                              });

                              props.onChangeRowsPerPage(event);
                            }}
                            nextIconButtonProps={{
                              disabled:
                                listOptions.pagination.page + 1 >=
                                Math.ceil(
                                  totalActions / listOptions.pagination.size
                                ),
                            }}
                            backIconButtonProps={{
                              disabled: listOptions.pagination.page <= 0,
                            }}
                          />
                        );
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid item lg={12} xs={12}>
                {successEditAction && (
                  <Grid
                    style={{
                      width: "100%",
                    }}
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justify="center"
                    className={classes.success}
                  >
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("editSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {emailSentSuccess && (
                  <Grid
                    style={{
                      width: "100%",
                    }}
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justify="center"
                    className={classes.success}
                  >
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("sentSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {actionDeleteSuccess && (
                  <Grid
                    style={{
                      width: "100%",
                    }}
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justify="center"
                    className={classes.success}
                  >
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("deleteSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {focalPointUpdateSuccess && (
                  <Grid
                    style={{
                      width: "100%",
                    }}
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justify="center"
                    className={classes.success}
                  >
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("menu.actions.bulkFocalPoint.success").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}

const withRouterActions = withRouter(Actions);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ActionsConnect = connect(null, mapDispatchToProps)(withRouterActions);

export default withTranslation()(ActionsConnect);
